// Rendering für Editor-Dokumente (Markdown) der Unterweisungs-Bibliothek.
// Platzhalter werden vor dem Rendern ersetzt. Rohes HTML im Quelltext wird von
// markdown-it escaped (html: false), daher ist kein zusätzlicher Sanitizer nötig.
import MarkdownIt from 'markdown-it'

const md = new MarkdownIt({ html: false, linkify: true })

export interface PlaceholderContext {
  orgName?: string
}

const BLANK = '____________________'

/**
 * Ersetzt die unterstützten Platzhalter durch Werte der Einrichtung.
 * Unbekannte Platzhalter werden zu einer Ausfüll-Linie, damit Dokumente mit
 * weiteren Feldern (z. B. durchführende Person) wie ein Formular aussehen.
 */
export function fillPlaceholders(text: string, ctx: PlaceholderContext): string {
  return String(text || '')
    .replace(/\{\{\s*einrichtung\s*\}\}/gi, ctx.orgName || BLANK)
    .replace(/\{\{\s*datum\s*\}\}/gi, new Date().toLocaleDateString('de-DE'))
    .replace(/\{\{[^{}]*\}\}/g, BLANK)
}

export function renderInstructionMarkdown(text: string, ctx: PlaceholderContext): string {
  return md.render(fillPlaceholders(text, ctx))
}

/** Für den Editor: rendert ohne Platzhalter-Ersetzung. */
export function renderMarkdownRaw(text: string): string {
  return md.render(String(text || ''))
}

// ── HTML → Markdown ───────────────────────────────────────────────────────
// Gegenrichtung für den WYSIWYG-Editor: Der Editor arbeitet mit HTML,
// gespeichert wird weiterhin Markdown. Übernommen wird ausschließlich, was das
// Dokumentformat kennt (Überschriften, Betonung, Listen, Tabellen, Zitate,
// Links, Trennlinien) — alles andere fällt weg. Dadurch bleibt das Erscheinungs-
// bild einheitlich, und eingefügte Fremdformate (z. B. aus Word) werden bereinigt.

// Elemente, deren Inhalt kein Dokumenttext ist und komplett entfällt.
const DROPPED_TAGS = ['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'IFRAME', 'OBJECT']

const PLACEHOLDER_PATTERN = /\{\{[^{}]*\}\}/g

function escapeSegment(text: string): string {
  return text
    .replace(/([\\*_[\]|])/g, '\\$1')
    .replace(/\s+/g, ' ')
}

function escapeText(text: string): string {
  // Platzhalter bleiben unangetastet: Ein Backslash vor dem Unterstrich in
  // {{durchfuehrende_person}} würde die Ersetzung beim Anzeigen verhindern.
  const source = String(text)
  let out = ''
  let last = 0
  let match: RegExpExecArray | null
  PLACEHOLDER_PATTERN.lastIndex = 0
  while ((match = PLACEHOLDER_PATTERN.exec(source)) !== null) {
    out += escapeSegment(source.slice(last, match.index)) + match[0]
    last = match.index + match[0].length
  }
  return out + escapeSegment(source.slice(last))
}

function inlineToMarkdown(node: Node): string {
  if (node.nodeType === 3) return escapeText(node.textContent || '')
  if (node.nodeType !== 1) return ''

  const el = node as HTMLElement
  if (DROPPED_TAGS.indexOf(el.tagName) >= 0) return ''
  const inner = childrenToMarkdown(el)

  switch (el.tagName) {
    case 'BR':
      // Zwei Leerzeichen vor dem Umbruch = harter Zeilenumbruch in Markdown.
      return '  \n'
    case 'STRONG':
    case 'B':
      return inner.trim() ? `**${inner.trim()}**` : ''
    case 'EM':
    case 'I':
      return inner.trim() ? `*${inner.trim()}*` : ''
    case 'CODE':
      return inner.trim() ? `\`${inner.trim()}\`` : ''
    case 'A': {
      const href = el.getAttribute('href') || ''
      return href && inner.trim() ? `[${inner.trim()}](${href})` : inner
    }
    default:
      return inner
  }
}

function childrenToMarkdown(el: HTMLElement): string {
  let out = ''
  el.childNodes.forEach((child) => { out += inlineToMarkdown(child) })
  return out
}

/** Zellinhalt einer Tabelle: Pipes maskieren, Umbrüche zu Leerzeichen. */
function cellToMarkdown(el: HTMLElement): string {
  return childrenToMarkdown(el).replace(/\n/g, ' ').replace(/\s+/g, ' ').trim()
}

function tableToMarkdown(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr'))
  if (!rows.length) return ''

  const matrix = rows.map((tr) =>
    Array.from(tr.children).map((cell) => cellToMarkdown(cell as HTMLElement))
  )
  const columns = matrix.reduce((max, row) => Math.max(max, row.length), 0)
  if (!columns) return ''

  const pad = (row: string[]) => {
    const filled = row.slice(0, columns)
    while (filled.length < columns) filled.push('')
    return `| ${filled.join(' | ')} |`
  }

  // Markdown-Tabellen brauchen immer eine Kopfzeile; hat die Tabelle keine,
  // wird die erste Zeile als Kopf verwendet.
  const head = pad(matrix[0])
  const divider = `| ${Array(columns).fill('---').join(' | ')} |`
  const body = matrix.slice(1).map(pad)
  return [head, divider, ...body].join('\n')
}

function listToMarkdown(list: HTMLElement, depth: number): string {
  const ordered = list.tagName === 'OL'
  const indent = '  '.repeat(depth)
  const lines: string[] = []

  Array.from(list.children).forEach((child, idx) => {
    if (child.tagName !== 'LI') return
    const li = child as HTMLElement
    const nested: string[] = []

    // Verschachtelte Listen aus dem Text herauslösen und eingerückt anhängen.
    Array.from(li.children).forEach((sub) => {
      if (sub.tagName === 'UL' || sub.tagName === 'OL') {
        nested.push(listToMarkdown(sub as HTMLElement, depth + 1))
        li.removeChild(sub)
      }
    })

    const marker = ordered ? `${idx + 1}.` : '-'
    const text = childrenToMarkdown(li).replace(/\n/g, ' ').trim()
    lines.push(`${indent}${marker} ${text}`)
    nested.forEach((n) => lines.push(n))
  })

  return lines.join('\n')
}

function blockToMarkdown(node: Node): string {
  if (node.nodeType === 3) {
    const text = escapeText(node.textContent || '').trim()
    return text ? text + '\n\n' : ''
  }
  if (node.nodeType !== 1) return ''

  const el = node as HTMLElement
  if (DROPPED_TAGS.indexOf(el.tagName) >= 0) return ''

  switch (el.tagName) {
    case 'H1':
      return `# ${childrenToMarkdown(el).trim()}\n\n`
    case 'H2':
      return `## ${childrenToMarkdown(el).trim()}\n\n`
    case 'H3':
    case 'H4':
    case 'H5':
    case 'H6':
      return `### ${childrenToMarkdown(el).trim()}\n\n`
    case 'UL':
    case 'OL': {
      const list = listToMarkdown(el, 0)
      return list ? list + '\n\n' : ''
    }
    case 'TABLE': {
      const table = tableToMarkdown(el as HTMLTableElement)
      return table ? table + '\n\n' : ''
    }
    case 'BLOCKQUOTE': {
      const inner = blocksToMarkdown(el).trim()
      if (!inner) return ''
      return inner.split('\n').map((line) => (line ? `> ${line}` : '>')).join('\n') + '\n\n'
    }
    case 'HR':
      return '---\n\n'
    case 'PRE':
      return '```\n' + (el.textContent || '').trim() + '\n```\n\n'
    case 'P':
    case 'DIV': {
      // Container, die selbst Blöcke enthalten (Editor-Artefakte), aufklappen.
      const hasBlockChild = Array.from(el.children).some((c) =>
        ['P', 'DIV', 'UL', 'OL', 'TABLE', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'HR'].indexOf(c.tagName) >= 0
      )
      if (hasBlockChild) return blocksToMarkdown(el)
      const text = childrenToMarkdown(el).trim()
      return text ? text + '\n\n' : ''
    }
    default: {
      const text = childrenToMarkdown(el).trim()
      return text ? text + '\n\n' : ''
    }
  }
}

function blocksToMarkdown(root: HTMLElement): string {
  let out = ''
  root.childNodes.forEach((child) => { out += blockToMarkdown(child) })
  return out
}

/** Wandelt den HTML-Inhalt des Editors in Markdown zum Speichern um. */
export function htmlToMarkdown(html: string): string {
  const container = document.createElement('div')
  container.innerHTML = String(html || '')
  return blocksToMarkdown(container)
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}
